import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, Route, Routes, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { ResponsiveContainer, Bar, BarChart, CartesianGrid, PieChart, Pie, Cell, XAxis, YAxis, Tooltip } from 'recharts';
import { DollarSign, ShoppingBag, Users, Palette, Sparkles, Mail, Menu, LayoutDashboard, Settings as SettingsIcon, Bell, TrendingUp, Search, Receipt, Package, Flag, ShieldCheck, Crown, FileText } from 'lucide-react';
import { useCurrency } from '@/context/CurrencyContext';
import SEO from '@/components/SEO';
import { Kpi } from './admin/_shared';
import CustomerDesignsSection from './admin/CustomerDesignsSection';
import OrdersSection from './admin/OrdersSection';
import UsersSection from './admin/UsersSection';
import DesignsSection from './admin/DesignsSection';
import CommunitySection from './admin/CommunitySection';
import ReportsSection from './admin/ReportsSection';
import PagesSection from './admin/PagesSection';
import MessagesSection from './admin/MessagesSection';
import ProductsSection, { CatalogSection } from './admin/ProductsSection';
import AnalyticsSection from './admin/AnalyticsSection';
import SecuritySection from './admin/SecuritySection';
import SettingsSection from './admin/SettingsSection';
import MonetizationSection from './admin/MonetizationSection';
import LoyaltyPremiumSection from './admin/LoyaltyPremiumSection';

const STATUS_COLORS = { placed: '#7DD3FC', in_production: '#FBBF24', shipped: '#A78BFA', delivered: '#34D399', cancelled: '#FB7185' };

const NAV = [
  { to: '/admin', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/admin/analytics', label: 'Analytics', icon: TrendingUp },
  { to: '/admin/monetization', label: 'Monetization', icon: Receipt },
  { to: '/admin/loyalty', label: 'Loyalty & Premium', icon: Crown },
  { to: '/admin/orders', label: 'Orders', icon: ShoppingBag },
  { to: '/admin/products', label: 'Products', icon: Package },
  { to: '/admin/catalog', label: 'Catalog', icon: SettingsIcon },
  { to: '/admin/users', label: 'Users', icon: Users },
  { to: '/admin/designs', label: 'Designs', icon: Palette },
  { to: '/admin/customer-designs', label: 'Customer Designs', icon: Sparkles },
  { to: '/admin/community', label: 'Community', icon: Users },
  { to: '/admin/reports', label: 'Reports', icon: Flag },
  { to: '/admin/pages', label: 'Pages', icon: FileText },
  { to: '/admin/messages', label: 'Messages', icon: Mail },
  { to: '/admin/security', label: 'Security', icon: ShieldCheck },
  { to: '/admin/settings', label: 'Settings', icon: SettingsIcon },
];

const Sidebar = ({ unreadContact }) => (
  <nav className="flex flex-col gap-1">
    {NAV.map(n => (
      <NavLink
        key={n.to}
        to={n.to}
        end={n.end}
        data-testid={`admin-nav-${n.label.toLowerCase()}-link`}
        className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition ${isActive ? 'jw-panel font-medium' : 'hover:opacity-80'}`}
        style={({ isActive }) => ({ color: isActive ? 'var(--jw-text)' : 'var(--jw-text-muted)' })}
      >
        <n.icon className="w-4 h-4" />
        <span className="flex-1">{n.label}</span>
        {n.label === 'Messages' && unreadContact > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full jw-gradient-bg text-white">{unreadContact}</span>
        )}
      </NavLink>
    ))}
  </nav>
);

const AdminLayout = () => {
  const [stats, setStats] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const contentRef = useRef(null);

  const loadStats = useCallback(() => {
    api.get('/admin/stats').then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  // Reset scroll AND refresh stats every time the admin route changes so
  // the reading pane always shows the top of the new section with fresh data.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    if (contentRef.current) contentRef.current.scrollTop = 0;
    loadStats();
    setDrawerOpen(false);
  }, [location.pathname, loadStats]);

  const unread = stats?.unread_contact || 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <SEO title="Admin Dashboard" description="Jwalek admin dashboard" path="/admin" noindex />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Admin</h1>
          <p className="text-sm" style={{ color: 'var(--jw-text-muted)' }}>Manage orders, users, designs, and site content.</p>
        </div>
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="lg:hidden" data-testid="admin-mobile-nav-button"><Menu className="w-5 h-5" /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64">
            <SheetTitle className="sr-only">Admin navigation</SheetTitle>
            <div className="mt-6" onClick={() => setDrawerOpen(false)}>
              <Sidebar unreadContact={unread} />
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-6">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <Sidebar unreadContact={unread} />
          </div>
        </aside>
        <div ref={contentRef} key={location.pathname} className="min-w-0" data-testid="admin-content-pane">
          <Outlet context={{ stats, refreshStats: loadStats }} />
        </div>
      </div>
    </div>
  );
};

const Overview = () => {
  const { stats } = useOutletContext();
  const { format } = useCurrency();
  const navigate = useNavigate();

  if (!stats) return <div style={{ color: 'var(--jw-text-muted)' }}>Loading...</div>;

  const statusData = Object.entries(stats.status_counts || {}).map(([status, count]) => ({ status, count, fill: STATUS_COLORS[status] || '#888' }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi icon={DollarSign} label="Revenue" value={format(stats.revenue)} sub={`AOV: ${format(stats.aov)}`} testId="admin-kpi-revenue" tone="brand" />
        <Kpi icon={ShoppingBag} label="Orders" value={stats.total_orders} sub={`${stats.archived_orders} archived`} testId="admin-kpi-orders" />
        <Kpi icon={Users} label="Customers" value={stats.total_users} sub={`${stats.archived_users} archived`} testId="admin-kpi-users" />
        <Kpi icon={Palette} label="Designs" value={stats.total_designs} testId="admin-kpi-designs" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="jw-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Orders — last 14 days</h3>
            <span className="text-xs" style={{ color: 'var(--jw-text-subtle)' }}>{stats.daily?.length || 0} data points</span>
          </div>
          <div style={{ width: '100%', height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={stats.daily}>
                <CartesianGrid stroke="var(--jw-border)" vertical={false} />
                <XAxis dataKey="day" stroke="var(--jw-text-muted)" fontSize={11} />
                <YAxis stroke="var(--jw-text-muted)" fontSize={11} />
                <Tooltip contentStyle={{ background: 'var(--jw-bg-alt)', border: '1px solid var(--jw-border)', borderRadius: 8 }} />
                <Bar dataKey="orders" fill="#FF6B35" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="jw-card p-5">
          <h3 className="font-semibold mb-3">Order status</h3>
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={statusData} dataKey="count" nameKey="status" innerRadius={45} outerRadius={80} paddingAngle={2}>
                  {statusData.map((s, i) => <Cell key={i} fill={s.fill} />)}
                </Pie>
                <Tooltip contentStyle={{ background: 'var(--jw-bg-alt)', border: '1px solid var(--jw-border)', borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1 mt-1">
            {statusData.map(s => (
              <div key={s.status} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: s.fill }} /> <span style={{ color: 'var(--jw-text-muted)' }} className="capitalize">{s.status.replace('_', ' ')}</span></span>
                <span>{s.count}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="jw-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Latest orders</h3>
          <Button size="sm" variant="ghost" onClick={() => navigate('/admin/orders')} data-testid="admin-view-all-orders-button">View all</Button>
        </div>
        <div className="divide-y" style={{ borderColor: 'var(--jw-border)' }}>
          {(stats.latest_orders || []).map(o => (
            <div key={o.id} className="py-3 flex items-center gap-3">
              <div>
                <p className="text-sm font-mono">{o.order_number}</p>
                <p className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{o.user?.name} · {new Date(o.created_at).toLocaleDateString()}</p>
              </div>
              <div className="ml-auto flex items-center gap-3">
                <Badge variant="secondary" className="capitalize text-xs">{o.status.replace('_', ' ')}</Badge>
                <span className="font-semibold text-sm">{format(o.total)}</span>
              </div>
            </div>
          ))}
          {(!stats.latest_orders || stats.latest_orders.length === 0) && <p className="text-sm py-4" style={{ color: 'var(--jw-text-muted)' }}>No orders yet.</p>}
        </div>
      </Card>
    </div>
  );
};

const AdminDashboard = () => (
  <Routes>
    <Route path="/" element={<AdminLayout />}>
      <Route index element={<Overview />} />
      <Route path="analytics" element={<AnalyticsSection />} />
      <Route path="monetization" element={<MonetizationSection />} />
      <Route path="loyalty" element={<LoyaltyPremiumSection />} />
      <Route path="orders" element={<OrdersSection />} />
      <Route path="customer-designs" element={<CustomerDesignsSection />} />
      <Route path="products" element={<ProductsSection />} />
      <Route path="catalog" element={<CatalogSection />} />
      <Route path="users" element={<UsersSection />} />
      <Route path="designs" element={<DesignsSection />} />
      <Route path="community" element={<CommunitySection />} />
      <Route path="reports" element={<ReportsSection />} />
      <Route path="pages" element={<PagesSection />} />
      <Route path="messages" element={<MessagesSection />} />
      <Route path="security" element={<SecuritySection />} />
      <Route path="settings" element={<SettingsSection />} />
    </Route>
  </Routes>
);

export default AdminDashboard;
